/*
 *---------------------------------------------------
 * Licensed Materials - Property of HCL Technologies
 *
 * HCL Commerce
 *
 * (C) Copyright HCL Technologies Limited 2021
 *
 *---------------------------------------------------
 */
//standard libraries
import { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import { Canceler } from "axios";
import { useTranslation } from "react-i18next";
//context
import { useOrdersValue } from "../context";
//custom libraries
import { useOrderHandler } from "./rest/use-order-handler";
import { TableAction, TableDialogOption } from "../constants/table";
import {
  ORDER_SEARCH_TABLE_ACTION,
  ORDER_SEARCH_TABLE_ACTIONS,
  ORDER_STATUS,
} from "../constants/order";
import { useShopOnBehalf } from "./use-shop-on-behalf";
import { useCustomerHandler } from "./rest";
import { ORDER } from "../constants/routes";
export interface useOrderSearchResultsRes {
  orders: any;
  getActions: (o: any) => TableAction[];
  dialogOption: TableDialogOption;
  t: any;
  handleViewDetails: Function;
}

export const useOrderSearchResults = (
  getOrder: any
): useOrderSearchResultsRes => {
  const history = useHistory();
  const { orders, setOrders } = useOrdersValue();
  const { lockOrderREST, unlockOrderREST } = useOrderHandler();
  const { updateCustomerRequest, getCustomerByIdRequest } =
    useCustomerHandler();
  const { shopOnBehalf } = useShopOnBehalf();
  const [dialogOption, setDialogOption] = useState<TableDialogOption | any>({});

  const cancels: any = { cancels: [] };
  const { t } = useTranslation();

  const toggleCustomerStatus = (order: any) => {
    const body = { status: order.buyer.status === 1 ? 0 : 1 };
    const [res, canceler] = updateCustomerRequest(body, order.buyer.id);
    cancels.cancels = canceler;
    res
      .then(() => {
        const [res, canceler] = getCustomerByIdRequest(order.buyer.id);
        cancels.cancels = canceler;
        return res;
      })
      .then((r: any) => {
        const buyer = r.data;
        if (orders.items) {
          const items = [...orders.items];
          items
            .filter((o: any) => {
              return o.memberId === buyer.id;
            })
            .forEach((o: any) => {
              o.buyer = buyer;
            });
          setOrders({ ...orders, items });
        }
      })
      .catch((e) => console.log(e));
  };

  const toggleLockUnLock = (order: any) => {
    if (order.isLocked !== undefined) {
      order.isLocked
        ? lockOrder(order.orderId, true)
        : order.checkLock
        ? unlockOrder(order.orderId)
        : lockOrder(order.orderId, false);
    }
  };
  const lockOrder = (orderId: string, takeOver: boolean) => {
    const [res, canceler] = lockOrderREST(orderId, takeOver);
    cancels.cancels = canceler;
    return res.then((r) => {
      return getOrder(orderId);
    });
  };

  const unlockOrder = (orderId: string) => {
    const [res, canceler] = unlockOrderREST(orderId);
    cancels.cancels = canceler;
    return res.then((r) => {
      if (r.status === 200) {
        return getOrder(orderId);
      }
    });
  };

  const getActions = (order: any): TableAction[] => {
    const actions: TableAction[] = [];
    for (const action of ORDER_SEARCH_TABLE_ACTIONS) {
      switch (action) {
        case ORDER_SEARCH_TABLE_ACTION.ACCESS_ACCOUNT: {
          if (order?.buyer?.status === 0) {
            break;
          }
          const ac: TableAction = {
            key: action,
            label: () => "Table.Actions.AccessCustomerAccount",
            action: () => {
              shopOnBehalf(order.buyer);
            },
          };
          actions.push(ac);
          break;
        }
        case ORDER_SEARCH_TABLE_ACTION.ENABLE_DISABLE_ACCOUNT: {
          const ac: TableAction = {
            key: action,
            label: () => {
              return order?.buyer?.status === 0
                ? "Table.Actions.EnableCustomerAccount"
                : "Table.Actions.DisableCustomerAccount";
            },
            action: () => {
              const dOption: TableDialogOption = {
                message: {
                  key:
                    "Customer.DisableEnableDialogHeading" + order.buyer.status,
                  params: {
                    user: order.buyer.logonId,
                  },
                },
                onConfirm: () => {
                  toggleCustomerStatus(order);
                  setDialogOption({});
                },
                onCancel: () => setDialogOption({}),
                confirmLabel:
                  "Table.Actions.ToggleAccountLock" + order.buyer.status,
                cancelLabel: "Table.Actions.Cancel",
              };
              setDialogOption(dOption);
            },
          };
          actions.push(ac);
          break;
        }
        case ORDER_SEARCH_TABLE_ACTION.LOCK_UNLOCK_ORDER: {
          if (order.status === ORDER_STATUS.PendingOrder) {
            const ac: TableAction = {
              key: action,
              label: () => {
                return order.isLocked
                  ? "OrderManagement.TakeOverOrder"
                  : order.checkLock
                  ? "OrderManagement.UnlockOrder"
                  : "OrderManagement.LockOrder";
              },
              action: () => {
                const dOption: TableDialogOption = {
                  message: {
                    key:
                      "OrderManagement.LockUnlockDialogHeading" +
                      order.checkLock +
                      order.isLocked,
                    params: {
                      order: order.orderId,
                    },
                  },
                  onConfirm: () => {
                    toggleLockUnLock(order);
                    setDialogOption({});
                  },
                  onCancel: () => setDialogOption({}),
                  confirmLabel:
                    "Table.Actions.ToggleLock" +
                    order.checkLock +
                    order.isLocked,
                  cancelLabel: "Table.Actions.Cancel",
                };
                setDialogOption(dOption);
              },
            };
            actions.push(ac);
          }
          break;
        }
        case ORDER_SEARCH_TABLE_ACTION.VIEW_ORDER: {
          const ac: TableAction = {
            key: action,
            label: () => "OrderManagement.ViewOrder",
            action: () => {
              const location = {
                pathname: ORDER + "/" + order.orderId,
                state: {
                  from: ORDER,
                  label: "Common.BackToOrderSearchResults",
                },
              };
              history.push(location);
            },
          };
          actions.push(ac);
          break;
        }
        default:
          throw new Error(`Table action is not supported: ${action}`);
      }
    }
    return actions;
  };

  const handleViewDetails = (order: any) => {
    const location = {
      pathname: `${ORDER}/${order.orderId}`,
      state: {
        from: ORDER,
        label: "Common.BackToOrderSearchResults",
      },
    };
    history.push(location);
  };

  useEffect(() => {
    return () => {
      cancels.cancels.forEach((cancel: Canceler) => {
        return cancel();
      });
    };
  }, []);

  return {
    orders,
    getActions,
    dialogOption,
    t,
    handleViewDetails,
  };
};
